/**
 * Unified-inbox display rules, as pure functions.
 *
 * These live outside the components because every one of them encodes a decision
 * from the plan that has to stay true — who may reply as which address, which
 * attribution a thread shows, when a mailbox is member-only — and a rule that can
 * only be checked by rendering a dialog is a rule that stops being checked.
 *
 * None of them is a security boundary. The server validates the From address on
 * every send (403 `from_address_denied`) and re-reads grants on every request;
 * these functions decide what to *offer*, so a stale client cannot show an option
 * that the server would then accept.
 */

import { extractAddress } from '../../lib/emailAddressList';

export const ROLE_ORDER = { viewer: 1, responder: 2, manager: 3 };

export const MY_INBOX_ID = '__my_inbox__';

/** localStorage key for the last-selected mailbox (Amendment 2). */
export const LAST_MAILBOX_KEY = 'nurturecrm.inbox.lastMailbox';

/**
 * Stable per-inbox colour, hashed from the id.
 *
 * Deterministic rather than stored: the dot only has to be consistent for one
 * user across sessions, and a `color` column on `unified_inboxes` would be a
 * schema field that exists solely so a dot does not move.
 */
const DEPT_HUES = [
    '#3E63DD', '#12A594', '#D6409F', '#E8590C',
    '#6E56CF', '#1A7F4E', '#946300', '#00A2C7',
];

export function inboxHue(inboxId) {
    let hash = 0;
    for (let i = 0; i < (inboxId || '').length; i += 1) {
        hash = ((hash << 5) - hash) + inboxId.charCodeAt(i);
        hash |= 0;
    }
    return DEPT_HUES[Math.abs(hash) % DEPT_HUES.length];
}


/** A unified inbox with no distro address pools its members' mailboxes instead. */
export function isMemberOnly(inbox) {
    return Boolean(inbox) && !inbox.distro_smtp && !inbox.distro_graph_user_id;
}

export function atLeast(role, minRole) {
    return (ROLE_ORDER[role] || 0) >= (ROLE_ORDER[minRole] || 0);
}

/**
 * The caller's effective role on an inbox.
 *
 * Every caller, including organization admins, gets the role on their own grant,
 * or null when they hold none. The server remains authoritative.
 */
export function roleFor(inbox, user) {
    if (!inbox || !user) return null;
    const grant = (inbox.access_grants || []).find(g => g.user_id === user.user_id);
    return grant?.role || null;
}

/** Is this caller one of the inbox's member mailboxes (not merely a grantee)? */
export function isMember(inbox, user) {
    if (!inbox || !user) return false;
    return (inbox.member_user_ids || []).includes(user.user_id);
}

/**
 * From-address options for the reply/compose composer.
 *
 * Mirrors the server's `_resolve_allowed_from` deliberately rather than trusting
 * it silently: offering an address the server will reject produces a 403 the user
 * cannot act on, and hiding one it would accept quietly removes a capability the
 * admin configured.
 *
 * Three rules, in order:
 *   1. `enforce_distro_reply` on a distro inbox ⇒ the distro is the ONLY option.
 *      The toggle governs this composer, never detection — a personal-address
 *      reply sent from Outlook is still ingested and flagged.
 *   2. Otherwise the distro (if any) plus the ONE member address this caller
 *      owns. Never another member's address.
 *   3. Member-only inbox ⇒ every pooled member address (#576 QA decision,
 *      2026-09-17): the grants decide who may act on the inbox, the pool decides
 *      what may be sent as — so a granted responder/manager who is not a member
 *      is no longer read-only. The server still validates the From on every
 *      send, so an address whose consent died since load fails honestly at
 *      send time. Legacy graph-id members cannot be resolved client-side; the
 *      server remains the only offer there (unchanged, permissive).
 */
export function fromOptions(inbox, user, { ownAddress, sendableAddresses } = {}) {
    if (!inbox) return [];
    const settings = inbox.reply_settings || {};
    const distro = (inbox.distro_smtp || '').toLowerCase() || null;
    const own = (ownAddress || '').toLowerCase() || null;

    if (distro && settings.enforce_distro_reply !== false) {
        return [{ address: distro, kind: 'distro', label: distro }];
    }

    const options = [];
    if (distro) options.push({ address: distro, kind: 'distro', label: distro });
    if (own && own !== distro && isPooledMember(inbox, user, own)) {
        options.push({
            address: own,
            kind: 'member',
            label: own,
            note: 'Recipients see your personal address, and the thread is flagged as a personal reply.',
        });
    }

    // Member-only: pooled team mailboxes are offered to every granted caller
    // (rule 3). ``member_smtps`` is the only client-resolvable pool — the list
    // payload carries no graph-id resolution, mirroring the server rule where
    // a live token decides.
    if (isMemberOnly(inbox) && atLeast(roleFor(inbox, user), 'responder')) {
        const offered = new Set(options.map(o => o.address));
        for (const raw of inbox.member_smtps || []) {
            const address = (raw || '').toLowerCase();
            if (!address || offered.has(address)) continue;
            options.push({
                address,
                kind: 'member',
                label: address,
                pool: true,
                note: 'A pooled team mailbox. Recipients see this address, not yours.',
            });
            offered.add(address);
        }
    }
    // The server has the last word on WHICH of these can actually send.
    //
    // Everything above is built from `member_smtps`, a DECLARED pool: an
    // admin types an address and it is listed whether or not its owner has
    // consented, and until #703 it stayed listed after revocation. The send
    // path resolves a narrower set — it subtracts revoked addresses and, for
    // the app-only fallback, keeps only mailboxes that can actually send —
    // so without this the picker offers identities the server answers 422
    // for. #702 made the server right and left this list alone (#700).
    //
    // `undefined` means "no answer yet, or the request failed", and is NOT
    // the same as an empty array: filtering on a failed request would lock
    // the user out of composing. An authoritative `[]` does block it, which
    // is the honest outcome when nothing is sendable.
    if (!sendableAddresses) return options;
    const sendable = new Set(sendableAddresses.map((a) => (a || '').toLowerCase()));
    return options.filter((option) => sendable.has(option.address.toLowerCase()));
}

/**
 * Is the caller's own address actually pooled into this inbox?
 *
 * Guards the one personal option in ``fromOptions``: the caller's own address
 * is offered as a personal identity only when it really is pooled. Since the
 * #576 QA decision, pooled team mailboxes are additionally offered to granted
 * callers on a member-only inbox via ``member_smtps`` directly — this function
 * stays about *whose* address it is.
 *
 * Legacy graph-id members (`member_graph_ids`) cannot be resolved to addresses
 * client-side, so an inbox that still pools any is treated permissively — the
 * server's From validation stays the gate there, exactly as before.
 */
function isPooledMember(inbox, user, own) {
    const memberSmtps = (inbox.member_smtps || []).map(a =>
        (a || '').toLowerCase()
    );
    if (memberSmtps.includes(own)) return true;
    if (user?.user_id && (inbox.member_user_ids || []).includes(user.user_id)) {
        return true;
    }
    return (inbox.member_graph_ids || []).length > 0;
}

/** The default From: the distro when there is one, else the caller's own mailbox. */
export function defaultFrom(options) {
    const distro = options.find(o => o.kind === 'distro');
    return (distro || options[0] || {}).address || null;
}

/**
 * May this caller reply in this inbox at all?
 *
 * Viewers never can. Everyone else needs at least one usable From address —
 * their own pooled mailbox, the distro, or (since the #576 QA decision) any
 * pooled member mailbox on a member-only inbox.
 */
export function canReply(inbox, user, { ownAddress } = {}) {
    const role = roleFor(inbox, user);
    if (!atLeast(role, 'responder')) return false;
    return fromOptions(inbox, user, { ownAddress }).length > 0;
}

/**
 * Why the composer is hidden, for the disabled affordance's tooltip.
 *
 * Returning null means it is not hidden. A tooltip that says nothing useful is
 * worse than none: "you cannot reply" invites a support ticket, "only members can
 * reply in a member-level inbox" tells the admin what to change.
 */
export function replyBlockedReason(inbox, user, { ownAddress } = {}) {
    const role = roleFor(inbox, user);
    if (!role) return 'You do not have access to this inbox.';
    if (!atLeast(role, 'responder')) {
        return 'Your access to this inbox is read-only.';
    }
    if (fromOptions(inbox, user, { ownAddress }).length === 0) {
        return isMemberOnly(inbox)
            ? 'No pooled mailbox on this inbox is currently available to send from.'
            : 'No address on this inbox is available to you.';
    }
    return null;
}

/**
 * Compute the recipients for a reply to one message in a thread.
 *
 * `ownAddresses` is the set of addresses the current composer is sending FROM
 * (the signed-in user and the selected From address). Reply All must never
 * send a copy back to the mailbox that is sending the reply. The returned
 * arrays are strings because the caller converts them into the typeahead's
 * `{ email }` shape at the component boundary.
 *
 * Reply All mirrors Outlook:
 *   - To = original sender + every original To recipient, minus self/sender.
 *   - Cc = every original Cc recipient, minus self/sender/duplicates.
 *   - Bcc = every original Bcc recipient, minus self/sender/duplicates.
 *
 * `hasBcc` tells the caller whether the CC/BCC toggle should auto-open so the
 * BCC field is visible without an extra click.
 *
 * Reply to our OWN message (outbound, or sent from one of `ownAddresses`) goes
 * to the people it was sent to, minus self, the way every mail client does it.
 * It used to push the sender unfiltered, which addressed the reply to our own
 * mailbox. That was visible and editable while the To row showed. The anchored
 * reply (#659) has no To row, so there it would deliver silently to ourselves.
 */
export function computeReplyRecipients(entry, { mode = 'reply', ownAddresses = [] } = {}) {
    const own = new Set(
        ownAddresses
            .filter(Boolean)
            // extractAddress, not a bare trim+lowercase: ownAddresses can
            // carry a display form (`Name <a@b>`) the same way from_address
            // can, and a form-mismatched duplicate would survive the filter
            // and put the sender in their own To list.
            .map(address => extractAddress(address))
            .filter(Boolean)
    );
    // extractAddress, not a bare trim: the personal Outlook sync stores
    // from_email as `Name <a@b>`, and Graph rejects a display form at send
    // time — so the seeded To entry has to be the bare address.
    const sender = extractAddress(entry?.from_address || entry?.from_email);
    const to = [];
    const seenTo = new Set();
    const addTo = address => {
        const value = extractAddress(address);
        const normalized = value.toLowerCase();
        if (!value || own.has(normalized) || seenTo.has(normalized)) return;
        seenTo.add(normalized);
        to.push(value);
    };

    const isOwnMessage = entry?.direction === 'outbound' || (sender && own.has(sender));
    if (mode === 'reply_all') {
        addTo(sender);
        (entry?.to_emails || []).forEach(addTo);
    } else if (isOwnMessage) {
        (entry?.to_emails || []).forEach(addTo);   // addTo already drops `own`
    } else if (sender) {
        to.push(sender);
    }

    const cc = [];
    const seenCc = new Set([...seenTo]);
    if (mode === 'reply_all') {
        (entry?.cc_emails || []).forEach(address => {
            // extractAddress, as for To: a display-form Cc (`Name <a@b>`) was
            // never matched against own/seen, and Graph rejects that form.
            const value = extractAddress(address);
            const normalized = value.toLowerCase();
            if (!value || own.has(normalized) || seenCc.has(normalized)) return;
            seenCc.add(normalized);
            cc.push(value);
        });
    }

    const bcc = [];
    let hasBcc = false;
    if (mode === 'reply_all') {
        const seenBcc = new Set([...seenCc]);
        (entry?.bcc_emails || []).forEach(address => {
            const value = extractAddress(address);
            const normalized = value.toLowerCase();
            if (!value || own.has(normalized) || seenBcc.has(normalized)) return;
            seenBcc.add(normalized);
            bcc.push(value);
        });
        hasBcc = bcc.length > 0;
    }

    return { to, cc, bcc, hasBcc };
}

/**
 * What an implicit reply (#659) will do, resolved BEFORE the composer opens.
 *
 * `name` is who the header says the reply goes to, so it comes from the same
 * `to` the send uses. The sender's name on our own outbound message would say
 * "Reply to <me>". `displayName(entry)` supplies the lane's name for the
 * sender (`parseFromName` on unified, `MailRow.senderName` on personal).
 *
 * `reason` is non-null when there is nobody to reply to. The `↩` must then
 * refuse, rather than open a composer that has no To row and cannot send.
 */
export function resolveReplyTarget(entry, { ownAddresses = [], displayName } = {}) {
    const { to } = computeReplyRecipients(entry, { mode: 'reply', ownAddresses });
    if (!to.length) return { to, name: null, reason: 'No recipient to reply to' };
    const sender = extractAddress(entry?.from_address || entry?.from_email);
    // A recipient's display name, when the row stored `Name <a@b>`: the
    // header reads "Reply to Ada", not the bare lowercased address.
    const recipientName = (address) => {
        const raw = (entry?.to_emails || []).find(v => extractAddress(v) === address) || '';
        const m = String(raw).match(/^\s*"?([^"<]+?)"?\s*<[^<>]+>\s*$/);
        return m ? m[1].trim() : address;
    };
    const first = to[0] === sender ? (displayName?.(entry) || to[0]) : recipientName(to[0]);
    return {
        to,
        name: to.length > 1 ? `${first} +${to.length - 1}` : first,
        reason: null,
    };
}


/** Whole hours/days since a timestamp, for the awaiting-reply SLA line. */
export function elapsedLabel(since, now = new Date()) {
    if (!since) return '';
    const then = new Date(since);
    if (Number.isNaN(then.getTime())) return '';
    const minutes = Math.max(0, Math.floor((now - then) / 60000));
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 48) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
}


/**
 * Subscription health for the banner: `healthy` | `degraded` | `none`.
 *
 * `degraded` rather than a raw count, because "3 of 4 active" needs a judgement
 * to be actionable and the judgement is the same everywhere it is shown.
 *
 * Any `degraded` subscription makes the inbox degraded, whatever `active` says
 * (#368). The server counts a row degraded when Graph reported missed
 * notifications or when it has delivered nothing for its silence window, and
 * both mean mail may be missing. Reading `active` alone hid them: a degraded
 * row beside a full set of active ones read as healthy, and degraded rows with
 * nothing active read as "no subscription at all" when they exist and are
 * simply not delivering.
 */
export function subscriptionHealth(stats) {
    const subs = stats?.subscriptions;
    if (!subs || !subs.expected) return 'none';
    if (subs.degraded > 0) return 'degraded';
    if (subs.active >= subs.expected) return 'healthy';
    if (subs.active === 0) return 'none';
    return 'degraded';
}

/**
 * Every problem a Microsoft verify run reported, as display lines.
 *
 * Verify returns ALL its errors on purpose ("the admin needs every problem at
 * once"), and the UI used to show only the first. With a missing subscription
 * and a degraded one, the degraded one never reached the admin.
 */
export function verifyErrorDetails(result) {
    const errors = Array.isArray(result?.verification_errors)
        ? result.verification_errors
        : [];
    const lines = errors.map(e => e?.detail).filter(Boolean);
    return lines.length ? lines : ['Verification failed'];
}

/** Sort a logical entry list client-side; the API returns newest-first. */
const REPLY_STATE_RANK = { awaiting: 2, none: 1, replied: 0 };

export const SORT_KEYS = {
    recipient_display_name: e => (e.recipient_display?.[0] || '').toLowerCase(),
    sender_email: e => (e.from_address || '').toLowerCase(),
    subject: e => (e.subject || '').toLowerCase(),
    received_at: e => e.last_activity_at || e.comm_date || '',
    // Unreplied first, then slowest reply — the order an admin chasing a backlog
    // actually wants, rather than a raw boolean sort that buries the tail. The
    // boolean never delivered what the comment promised; a rank does, and it
    // gives "none" (a thread we started) its own place between the two.
    replied: e => REPLY_STATE_RANK[e.reply_state ?? (e.replied_at ? 'replied' : 'awaiting')] ?? 0,
};

export function sortEntries(entries, sortKey, direction = 'desc') {
    const pick = SORT_KEYS[sortKey];
    if (!pick) return entries;
    const sign = direction === 'asc' ? 1 : -1;
    return [...entries].sort((a, b) => {
        const av = pick(a);
        const bv = pick(b);
        if (av === bv) return 0;
        return av > bv ? sign : -sign;
    });
}

/**
 * Extract a display name from an email entry.
 *
 * New records store `from_name` directly (added in the body_html threading
 * phase). Old records only have `from_email` in `"Name <addr>"` format, so we
 * parse that as a fallback.
 */
export function parseFromName(entry) {
    if (entry.from_name) return entry.from_name;
    // Fallback: parse "Name <addr>" from from_email
    // Handle quoted display names: "John, Jr." <john@example.com>
    const fromEmail = entry.from_email || '';
    const match = fromEmail.match(/^"?(.+?)"?\s*<.+>$/);
    if (match) {
        // Strip surrounding quotes if present
        return match[1].replace(/^"(.*)"$/, '$1').trim();
    }
    return '';
}

